import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Badge,
  EnhancedProductCard,
} from "@b2b/ui-components";
import { Heart, Search, X, RefreshCw, CheckCircle, XCircle } from "lucide-react";
import { favoritesService } from "@b2b/services";
import { useAuth } from "../../auth/Authprovider";
import type { FavoriteProduct } from "@b2b/services";

const FavoritesPage = () => {
  const navigate = useNavigate();
  const { userRole } = useAuth();
  const [favorites, setFavorites] = useState<FavoriteProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearchTerm, setAppliedSearchTerm] = useState("");

  useEffect(() => {
    fetchFavorites();
  }, []);

  const fetchFavorites = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await favoritesService.getFavorites();
      const favoritesData = response?.favorites ?? [];
      setFavorites(favoritesData);
    } catch (err: any) {
      console.error("Error fetching favorites:", err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load favorites"
      );
    } finally {
      setLoading(false);
    }
  };

  const showToast = useCallback((type: "success" | "error", message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 5000);
  }, []);

  const handleFavoriteToggle = async (
    productId: string,
    isFavorited: boolean
  ) => {
    try {
      if (isFavorited) {
        await favoritesService.addFavorite(productId);
        showToast("success", "Product added to favorites");
        fetchFavorites();
      } else {
        await favoritesService.removeFavorite(productId);
        showToast("success", "Product removed from favorites");
        setFavorites((prev) => prev.filter((fav) => fav.id !== productId));
      }
    } catch (err: any) {
      console.error("Error toggling favorite:", err);
      showToast(
        "error",
        err?.response?.data?.message || "Failed to update favorite"
      );
      throw err;
    }
  };

  const handleSearch = () => {
    setAppliedSearchTerm(searchTerm.trim());
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setAppliedSearchTerm("");
  };

  const handleRemoveFilter = () => {
    handleClearSearch();
  };

  const filteredFavorites = useMemo(() => {
    const q = appliedSearchTerm.toLowerCase();
    if (!q) return favorites;
    return favorites.filter((f) => {
      const name = f.name?.toLowerCase() ?? "";
      const sku = f.sku?.toLowerCase() ?? "";
      const seller = f.seller?.name?.toLowerCase() ?? "";
      const brand = f.brand?.toLowerCase() ?? "";
      return (
        name.includes(q) ||
        sku.includes(q) ||
        seller.includes(q) ||
        brand.includes(q)
      );
    });
  }, [favorites, appliedSearchTerm]);

  const activeFilters = useMemo(() => {
    const filters: string[] = [];
    if (appliedSearchTerm) filters.push(`Search: "${appliedSearchTerm}"`);
    return filters;
  }, [appliedSearchTerm]);

  /** Same card shape as `Pages/Common/ProductSearch.tsx` (EnhancedProductCard + grid). */
  const mappedProducts = useMemo(
    () =>
      filteredFavorites.map((favorite) => ({
        id: favorite.id,
        title: favorite.name,
        price: `$${Number(favorite.price).toFixed(2)}`,
        moq: favorite.moq ? `${favorite.moq} pieces` : "1 piece",
        score: 4.5,
        supplier: {
          id: favorite.seller?.id,
          name: favorite.seller?.name || "Supplier",
          rating: 4,
          verified: true,
        },
        image:
          favorite.thumbnailImage ||
          (favorite.images && favorite.images.length > 0
            ? favorite.images[0]
            : ""),
      })),
    [filteredFavorites]
  );

  const isBuyer = userRole === "buyer";

  return (
    <div className="w-full max-w-none mx-auto px-2 sm:px-3 lg:px-4 pb-8 pt-4 sm:pt-6 space-y-6">
      {toast && (
        <div
          className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg z-[9999] ${
            toast.type === "success"
              ? "bg-green-500 text-white"
              : "bg-red-500 text-white"
          }`}
        >
          <div className="flex items-center gap-2">
            {toast.type === "success" ? (
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
            ) : (
              <XCircle className="w-5 h-5 flex-shrink-0" />
            )}
            <p className="flex-1 font-medium text-sm">{toast.message}</p>
            <button
              type="button"
              onClick={() => setToast(null)}
              className="ml-2 hover:opacity-80 transition-opacity flex-shrink-0"
              aria-label="Close notification"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-foreground">My Favorites</h1>
          <p className="text-muted-foreground">
            {favorites.length === 0
              ? "You haven't favorited any products yet."
              : `You have ${favorites.length} favorite product${favorites.length !== 1 ? "s" : ""}.`}
          </p>
        </div>
      </div>

      {!loading && !error && favorites.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Search & Filters</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
                <Input
                  type="text"
                  placeholder="Search by product name, SKU, seller, or brand..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSearch();
                  }}
                  className="pl-10"
                />
              </div>
              <Button
                type="button"
                onClick={handleSearch}
                variant="outline"
                disabled={loading}
              >
                <Search className="mr-2 h-4 w-4" />
                Search
              </Button>
              <Button type="button" variant="outline" onClick={handleClearSearch}>
                <X className="mr-2 h-4 w-4" />
                Clear Filters
              </Button>
            </div>
            {activeFilters.length > 0 && (
              <div className="flex flex-wrap gap-2 items-center">
                <span className="text-sm text-muted-foreground">
                  Active filters:
                </span>
                {activeFilters.map((filter, index) => (
                  <Badge
                    key={index}
                    variant="secondary"
                    className="flex items-center gap-1 cursor-pointer"
                    onClick={() => handleRemoveFilter()}
                  >
                    {filter}
                    <X className="h-3 w-3" />
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>
            Your favorites
            {favorites.length > 0
              ? ` (${filteredFavorites.length}${
                  appliedSearchTerm ? ` of ${favorites.length}` : ""
                } shown)`
              : ""}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin mr-2" />
              <span>Loading favorites...</span>
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-600">
              <p>{error}</p>
              <Button
                type="button"
                variant="outline"
                className="mt-4"
                onClick={() => void fetchFavorites()}
              >
                Try again
              </Button>
            </div>
          ) : favorites.length === 0 ? (
            <div className="text-center py-8">
              <Heart className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-muted-foreground mb-2">No favorites yet</p>
              <p className="text-sm text-muted-foreground mb-6">
                Start exploring products and add them to your favorites.
              </p>
              <Button
                type="button"
                onClick={() => navigate("/search")}
                className="bg-orange-500 hover:bg-orange-600 text-white"
              >
                Browse products
              </Button>
            </div>
          ) : filteredFavorites.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">
                No favorites match your search criteria.
              </p>
              <Button
                type="button"
                variant="outline"
                className="mt-4"
                onClick={handleClearSearch}
              >
                Clear search
              </Button>
            </div>
          ) : (
            <div className="p-4 lg:p-6">
              <div className="grid items-start gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                {mappedProducts.map((product, index) => (
                  <EnhancedProductCard
                    key={`${product.id}-${index}`}
                    product={{
                      id: product.id,
                      title: product.title,
                      price: product.price,
                      moq: product.moq,
                      score: product.score,
                      supplier: product.supplier,
                      image: product.image,
                    }}
                    isFavorited={true}
                    onFavoriteToggle={
                      isBuyer ? handleFavoriteToggle : undefined
                    }
                  />
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FavoritesPage;
